import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  archiveTest, deleteTest, duplicateTest, errorDetail, fetchOverview, humanError, unarchiveTest,
} from '../api';
import { Icon } from '../components/icons';
import { TestCard } from '../components/TestCard';
import { WorkspaceShell } from '../components/WorkspaceShell';
import { withAdminOnly } from '../components/withAdminOnly';
import { useDebounced } from '../hooks';
import { FILTERS } from '../model';
import type { OverviewCounts, OverviewItem, TestStatus } from '../model';
import { takeNotice } from '../notices';
import type { WorkspaceNotice } from '../notices';

const PAGE_SIZE = 20;
type Filter = 'all' | TestStatus;

const TestsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const debouncedSearch = useDebounced(query.trim(), 250);
  const search = query.trim() ? debouncedSearch : ''; // очистка поля действует сразу
  const [filter, setFilter] = useState<Filter>('all');
  const [items, setItems] = useState<OverviewItem[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<OverviewCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const [notice, setNotice] = useState<WorkspaceNotice | null>(null);
  const [menuId, setMenuId] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [blocked, setBlocked] = useState<{ id: number; available: number } | null>(null);
  const requestRef = useRef(0);
  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Не в инициализаторе useState: под <React.StrictMode> компонент рендерится дважды, второй вызов вернул бы null.
    const pending = takeNotice();
    if (pending) setNotice(pending);
  }, []);

  /** Одно действие над тестом за раз: двойное нажатие не создаёт вторую копию. */
  const guarded = async (action: () => Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await action();
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const load = useCallback(async (skip: number) => {
    const request = ++requestRef.current; // устаревший ответ не должен затереть свежий
    if (skip === 0) setLoading(true);
    else setLoadingMore(true);
    setFailed(false);
    try {
      const data = await fetchOverview({
        status: filter === 'all' ? undefined : filter,
        search,
        skip,
        limit: PAGE_SIZE,
      });
      if (request !== requestRef.current) return;
      setItems((previous) => (skip === 0
        ? data.items
        : [...previous, ...data.items.filter((entry) => !previous.some((old) => old.id === entry.id))]));
      setTotal(data.total);
      setCounts(data.counts);
    } catch {
      if (request === requestRef.current) setFailed(true);
    } finally {
      if (request === requestRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [filter, search]);

  // Действия (копия, архив, возврат) перезагружают список с ТЕКУЩИМИ фильтром и поиском, а не с теми, что были
  // в отрисовке, где нажали кнопку: иначе после смены фильтра во время действия список подменится чужим.
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    void load(0);
  }, [load]);

  const showAll = () => {
    setQuery('');
    setFilter('all');
  };

  const copy = (item: OverviewItem) => guarded(async () => {
    try {
      const created = await duplicateTest(item.id);
      setMenuId(null);
      setNotice({
        kind: 'ok',
        text: `Готово: создана копия «${created.title}». Она в состоянии «Готовится» — сотрудники её пока не видят.`,
      });
      await loadRef.current(0);
    } catch (error) {
      setNotice({ kind: 'info', text: humanError(error, 'Не получилось сделать копию. Попробуйте ещё раз.') });
    }
  });

  const remove = (item: OverviewItem) => guarded(async () => {
    try {
      await deleteTest(item.id);
      setDeleteId(null);
      setMenuId(null);
      setBlocked(null);
      setItems((current) => current.filter((entry) => entry.id !== item.id));
      setNotice({ kind: 'ok', text: `Тест «${item.title}» удалён.` });
      await loadRef.current(0);
    } catch (error) {
      const status = (error as { response?: { status?: number } } | null)?.response?.status;
      if (status === 404) {
        setDeleteId(null);
        setMenuId(null);
        setItems((current) => current.filter((entry) => entry.id !== item.id));
        setNotice({ kind: 'info', text: 'Этот тест уже удалён. Список обновлён.' });
        await loadRef.current(0);
      } else {
        setNotice({ kind: 'info', text: humanError(error, 'Не получилось удалить тест. Попробуйте ещё раз.') });
      }
    }
  });

  const archive = (item: OverviewItem) => guarded(async () => {
    try {
      await archiveTest(item.id);
      setMenuId(null);
      setBlocked(null);
      setNotice({
        kind: 'ok',
        text: `Тест «${item.title}» убран в архив. Сотрудники его больше не видят. Вернуть его можно в списке: кнопка «В архиве».`,
      });
      await loadRef.current(0);
    } catch (error) {
      const detail = errorDetail(error);
      if (detail?.code === 'archive_blocked_by_live_assignments') {
        setBlocked({ id: item.id, available: Number(detail.available) || 0 });
      } else {
        setNotice({ kind: 'info', text: humanError(error, 'Не получилось убрать тест в архив. Попробуйте ещё раз.') });
      }
    }
  });

  const restore = (item: OverviewItem) => guarded(async () => {
    try {
      await unarchiveTest(item.id);
      setMenuId(null);
      setNotice({ kind: 'ok', text: `Тест «${item.title}» вернулся из архива.` });
      await loadRef.current(0);
    } catch (error) {
      setNotice({ kind: 'info', text: humanError(error, 'Не получилось вернуть тест из архива. Попробуйте ещё раз.') });
    }
  });

  const hasTests = counts === null || counts.all > 0;
  const help = FILTERS.find((entry) => entry.key === filter)?.help ?? '';

  return (
    <WorkspaceShell wide notice={notice} onCloseNotice={() => setNotice(null)}>
      <section data-guide="admin-tests-home" className="tw-screen tw-screen-list">
        <div className="tw-list-head">
          <h1>Тесты</h1>
          <p className="tw-lead" style={{ marginTop: 8 }}>
            Здесь собраны все тесты. Нажмите «Создать тест», чтобы сделать новый, или откройте готовый.
          </p>
        </div>

        <div className="tw-howto">
          <div className="tw-howto-item">
            <span className="tw-howto-n">1</span>
            <span><span className="tw-howto-t">Создайте тест</span><span className="tw-howto-s">Придумайте название и время</span></span>
          </div>
          <div className="tw-howto-item">
            <span className="tw-howto-n">2</span>
            <span><span className="tw-howto-t">Добавьте вопросы</span><span className="tw-howto-s">Свои или из готовых</span></span>
          </div>
          <div className="tw-howto-item">
            <span className="tw-howto-n">3</span>
            <span><span className="tw-howto-t">Дайте тест сотрудникам</span><span className="tw-howto-s">Всем или выбранным людям</span></span>
          </div>
        </div>

        <div className="tw-row tw-btn-row tw-list-cta">
          <button type="button" className="tw-btn" onClick={() => navigate('/admin/tests/unanswered')}>
            <Icon name="usercheck" />
            Непройденные вопросы
          </button>
          <button data-guide="admin-tests-create" type="button" className="tw-btn tw-btn-primary tw-btn-big" onClick={() => navigate('/admin/tests/new')}>
            <Icon name="plus" />
            Создать тест
          </button>
        </div>

        {hasTests && (
          <div className="tw-card tw-stack-sm">
            <div>
              <label className="tw-label" htmlFor="tw-search">Найти тест или сотрудника</label>
              <div className="tw-search">
                <Icon name="search" />
                <input
                  data-guide="admin-tests-search" id="tw-search"
                  className="tw-field"
                  type="text"
                  maxLength={200}
                  placeholder="Например: тормоза или Анна"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </div>
              <p className="tw-help" style={{ marginTop: 8 }}>
                Ищем по названию теста и по именам сотрудников, которым тест выдан.
              </p>
            </div>
            <div>
              <div className="tw-label">Показать</div>
              <div className="tw-row" role="group" aria-label="Какие тесты показать">
                {FILTERS.map((entry) => (
                  <button
                    key={entry.key}
                    type="button"
                    className="tw-chip"
                    aria-pressed={filter === entry.key}
                    onClick={() => setFilter(entry.key)}
                  >
                    {`${entry.label} (${counts ? counts[entry.key] : 0})`}
                  </button>
                ))}
              </div>
              <p className="tw-help" style={{ marginTop: 10 }}>{help}</p>
            </div>
          </div>
        )}

        {loading && items.length === 0 && !failed && (
          <div className="tw-card tw-empty" aria-busy="true">
            <p className="tw-help">Загружаем тесты…</p>
          </div>
        )}

        {failed && (
          <div className="tw-card tw-empty">
            <Icon name="info" size={44} large />
            <h2>Не получилось загрузить тесты</h2>
            <p className="tw-help">Проверьте соединение и попробуйте ещё раз.</p>
            <button type="button" className="tw-btn tw-btn-primary" onClick={() => void load(0)}>Повторить</button>
          </div>
        )}

        {items.map((item) => (
          <TestCard
            key={item.id}
            item={item}
            menuOpen={menuId === item.id}
            blocked={blocked && blocked.id === item.id ? blocked.available : null}
            busy={busy}
            onOpen={() => navigate(`/admin/tests/${item.id}`)}
            onAssign={() => navigate(`/admin/tests/${item.id}/assign`)}
            onToggleMenu={() => { setMenuId(menuId === item.id ? null : item.id); setDeleteId(null); }}
            onCopy={() => void copy(item)}
            onArchive={() => void archive(item)}
            onRestore={() => void restore(item)}
            onGoGiven={() => navigate(`/admin/tests/${item.id}?tab=given`)}
            deleting={deleteId === item.id}
            onDelete={() => { setDeleteId(item.id); setBlocked(null); }}
            onConfirmDelete={() => void remove(item)}
            onCancelDelete={() => setDeleteId(null)}
          />
        ))}

        {items.length < total && (
          <div className="tw-row tw-btn-row" style={{ justifyContent: 'center' }}>
            <button type="button" className="tw-btn" disabled={loading || loadingMore} onClick={() => void load(items.length)}>
              Показать ещё
            </button>
          </div>
        )}

        {!loading && !failed && items.length === 0 && counts !== null && counts.all > 0 && (
          <div className="tw-card tw-empty">
            <Icon name="search" size={44} large />
            <h2>Ничего не нашли</h2>
            <p className="tw-help">Проверьте, как написано название или имя, или покажите все тесты.</p>
            <button type="button" className="tw-btn tw-btn-primary" onClick={showAll}>Показать все тесты</button>
          </div>
        )}

        {!loading && !failed && counts !== null && counts.all === 0 && (
          <div className="tw-card tw-empty">
            <Icon name="file" size={44} large />
            <h2>Пока нет ни одного теста</h2>
            <p className="tw-help">Создайте первый тест: это займёт несколько минут. Мы подскажем каждый шаг.</p>
            <button type="button" className="tw-btn tw-btn-primary tw-btn-big" onClick={() => navigate('/admin/tests/new')}>
              Создать первый тест
            </button>
          </div>
        )}
      </section>
    </WorkspaceShell>
  );
};

export default withAdminOnly(TestsListPage);
